import { countries } from 'country-data-list';
import { Trie } from '@datastructures-js/trie';
import { CountryOption, CountryPhoneOption } from '../types';

export const CALLING_CODE_TRIE = new Trie();

export const CALLING_CODE_COUNTRY_MAP = new Map<string, Array<CountryPhoneOption>>();

export const PHONE_COUNTRY_MAP = new Map<string, CountryPhoneOption>();

export const RE_LEADING_ZEROES = /^0+/;

const sortedCountries = countries.all.sort((a, b) => {
  if (a.alpha2 === 'US') {
    return -1;
  }

  return a.name.localeCompare(b.name);
});

export const COUNTRY_OPTIONS: Array<CountryOption> = sortedCountries.reduce<Array<CountryOption>>(
  (result, { alpha2, emoji, name, status }) => {
    if (status !== 'assigned') {
      return result;
    }

    const label = `${emoji ?? '🌐'} ${name}`;

    const option: CountryOption = {
      label,
      selectedLabel: label,
      value: alpha2,
    };

    result.push(option);

    return result;
  },
  []
);

/* For calling codes with multiple countries, these options will be prioritised
 * for selection first when matching against input phone numbers. This ensures
 * that, for example, GB is selected when 44 is entered, rather than GG - the
 * code for Guernsey.
 */
const priorityCountryCodes = [
  'AQ',
  'AU',
  'CW',
  'FI',
  'FK',
  'GB',
  'GP',
  'MA',
  'NO',
  'RE',
  'RU',
  'US',
];

export const PHONE_COUNTRY_OPTIONS: Array<CountryPhoneOption> = sortedCountries.reduce<
  Array<CountryPhoneOption>
>((result, { alpha2, countryCallingCodes, emoji, name, status }) => {
  if (status !== 'assigned') {
    return result;
  }

  const [countryCallingCode] = countryCallingCodes;

  if (!countryCallingCode) {
    return result;
  }

  const option: CountryPhoneOption = {
    callingCode: countryCallingCode ?? '',
    label: `${emoji ?? '🌐'} ${name} (${countryCallingCode})`,
    selectedLabel: `${emoji ?? '🌐'} ${alpha2}`,
    value: alpha2,
  };

  result.push(option);

  PHONE_COUNTRY_MAP.set(alpha2, option);

  // Add the calling code into the lookup trie, with the '+' prefix removed
  const callingCodeKey = countryCallingCode.slice(1);

  CALLING_CODE_TRIE.insert(callingCodeKey);

  // Map the calling code to the possible countries
  const existingCountries = CALLING_CODE_COUNTRY_MAP.get(callingCodeKey) ?? [];

  /* If the country is in the priority list, add it to the front of the list
   * to ensure it is selected first when matching against input phone numbers.
   */
  const updatedCountries = priorityCountryCodes.includes(option.value)
    ? [option, ...existingCountries]
    : [...existingCountries, option];

  CALLING_CODE_COUNTRY_MAP.set(callingCodeKey, updatedCountries);

  return result;
}, []);
